import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from 'venn-ui-kit';
import { CombinePortfoliosWizard } from '../../CombinePortfoliosWizard/CombinePortfoliosWizard';
import { isNil } from 'lodash';
import type { LibrarySearchEntity } from 'venn-api';
import { useNavigateToStudio } from 'venn-components';
import type { Subject } from 'venn-state';
import { assertExhaustive } from 'venn-utils';
import type { CombinePortfoliosWizardConfiguration, OpenInDestination } from '../../CombinePortfoliosWizard/types';

const entityToSubject = (value: LibrarySearchEntity): Subject => ({
  portfolioId: value.portfolioId,
  fundId: value.fundId,
  privateFundId: value.privateFundId,
  privatePortfolioId: value.privatePortfolioId,
});

type PortfoliosTableActionProps = {
  selectedRows: LibrarySearchEntity[];
};

const PortfoliosTableAction: React.FC<PortfoliosTableActionProps> = ({ selectedRows }) => {
  const [combinePortfoliosConfiguration, setCombinePortfoliosConfiguration] =
    useState<CombinePortfoliosWizardConfiguration>();
  const navigateToReportLab = useNavigateToStudio('open in report lab RCM homepage', 'REPORT');
  const navigateToStudio = useNavigateToStudio('open in studio RCM homepage', 'TEARSHEET');

  const publicPortfolioInformation = useMemo(
    () =>
      selectedRows
        .map(({ portfolioId, clientName, householdName }) => ({ id: portfolioId, clientName, householdName }))
        .filter(({ id }) => !isNil(id)),
    [selectedRows],
  );
  const selectedRowsCount = selectedRows.length;

  const onOpenIn = (destination: OpenInDestination) => {
    if (publicPortfolioInformation.length !== selectedRows.length) {
      const subjects = selectedRows.map(entityToSubject);
      switch (destination) {
        case 'report_lab':
          navigateToReportLab(subjects);
          return;
        case 'studio':
          navigateToStudio(subjects);
          return;
        default:
          throw assertExhaustive(destination);
      }
    } else {
      setCombinePortfoliosConfiguration({
        portfolioInformation: publicPortfolioInformation,
        destination,
      });
    }
  };

  const actionBtnClass = 'text-[14px] uppercase font-bold text-venn-dark-blue hover:text-venn-dark-blue';

  return (
    <>
      <div className="px-[10px] h-[36px] flex items-center justify-start gap-[24px]">
        <div>{selectedRowsCount} selected</div>
        {selectedRowsCount ? (
          <div className="flex gap-[3px]">
            <Button variant="ghost" className={actionBtnClass} onClick={() => onOpenIn('report_lab')}>
              <Icon type="file-chart-column" />
              Open in Report lab ({selectedRowsCount})
            </Button>
            <Button variant="ghost" className={actionBtnClass} onClick={() => onOpenIn('studio')}>
              <Icon type="calculator-simple" />
              Open in Studio ({selectedRowsCount})
            </Button>
          </div>
        ) : (
          <div className="text-venn-grey-60">Select items to view actions</div>
        )}
      </div>
      {combinePortfoliosConfiguration && (
        <CombinePortfoliosWizard
          onClose={() => setCombinePortfoliosConfiguration(undefined)}
          configuration={combinePortfoliosConfiguration}
        />
      )}
    </>
  );
};

export default PortfoliosTableAction;
